import { absoluteUrl, DEFAULT_IMAGE, routeSeo } from './routeSeo'

function setMeta(attribute, key, content) {
  let meta = document.head.querySelector(`meta[${attribute}="${key}"]`)
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attribute, key)
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
}

function removeMeta(attribute, key) {
  document.head.querySelectorAll(`meta[${attribute}="${key}"]`).forEach(meta => meta.remove())
}

function setLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`
  let link = document.head.querySelector(selector)
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', rel)
    if (hreflang) link.setAttribute('hreflang', hreflang)
    document.head.appendChild(link)
  }
  link.setAttribute('href', href)
}

function clearAlternateLinks() {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(link => link.remove())
}

function setJsonLd(id, data) {
  let script = document.head.querySelector(`script[data-seo-schema="${id}"]`)
  if (!script) {
    script = document.createElement('script')
    script.type = 'application/ld+json'
    script.dataset.seoSchema = id
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(data)
}

function removeJsonLd(id) {
  const script = document.head.querySelector(`script[data-seo-schema="${id}"]`)
  if (script) script.remove()
}

export function updateSeoHead(route, router) {
  const registeredSeo = routeSeo[route.name]
  const fallbackSeo = routeSeo['not-found']
  const seo = registeredSeo || fallbackSeo
  const hasValidText = seo &&
    typeof seo.title === 'string' && seo.title.trim() &&
    typeof seo.description === 'string' && seo.description.trim()

  if (!hasValidText) {
    throw new Error(`Missing valid SEO title or description for route: ${route.name || 'unknown'}`)
  }

  document.title = seo.title
  setMeta('name', 'description', seo.description)

  if (!seo.indexable) {
    const canonical = document.head.querySelector('link[rel="canonical"]')
    if (canonical) canonical.remove()
    clearAlternateLinks()
    removeJsonLd('page')
    removeJsonLd('breadcrumb')
    removeMeta('property', 'og:url')
    setMeta('property', 'og:title', seo.title)
    setMeta('property', 'og:description', seo.description)
    setMeta('name', 'twitter:title', seo.title)
    setMeta('name', 'twitter:description', seo.description)
    setMeta('name', 'robots', 'noindex, follow')
    document.documentElement.setAttribute('lang', seo.lang || 'en')
    document.documentElement.setAttribute('dir', seo.lang === 'ar' ? 'rtl' : 'ltr')
    return
  }

  const canonicalUrl = absoluteUrl(seo.canonical || seo.path)
  setLink('canonical', canonicalUrl)
  setMeta('name', 'robots', 'index, follow')

  clearAlternateLinks()
  setLink('alternate', canonicalUrl, seo.lang)
  if (seo.alternate && routeSeo[seo.alternate]) {
    const alternateSeo = routeSeo[seo.alternate]
    const alternateUrl = absoluteUrl(alternateSeo.canonical || alternateSeo.path)
    setLink('alternate', alternateUrl, alternateSeo.lang)
    setLink(
      'alternate',
      seo.lang === 'en' ? canonicalUrl : alternateUrl,
      'x-default'
    )
  } else {
    setLink('alternate', canonicalUrl, 'x-default')
  }

  setMeta('property', 'og:type', 'website')
  setMeta('property', 'og:site_name', 'SplendApp Tools')
  setMeta('property', 'og:title', seo.title)
  setMeta('property', 'og:description', seo.description)
  setMeta('property', 'og:url', canonicalUrl)
  setMeta('property', 'og:image', DEFAULT_IMAGE)
  setMeta('property', 'og:locale', seo.lang === 'ar' ? 'ar_AR' : 'en_US')

  setMeta('name', 'twitter:card', 'summary')
  setMeta('name', 'twitter:title', seo.title)
  setMeta('name', 'twitter:description', seo.description)
  setMeta('name', 'twitter:image', DEFAULT_IMAGE)

  setJsonLd('page', route.name === 'home' ? {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'SplendApp Tools',
    url: canonicalUrl,
    description: seo.description,
    inLanguage: seo.lang
  } : {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: seo.title.replace(/ \| .+$/, ''),
    url: canonicalUrl,
    description: seo.description,
    inLanguage: seo.lang,
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Any',
    browserRequirements: 'Requires JavaScript and a modern web browser',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    }
  })

  if (route.name === 'home') {
    removeJsonLd('breadcrumb')
  } else {
    setJsonLd('breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: seo.lang === 'ar' ? 'الأدوات' : 'Tools Hub',
          item: absoluteUrl('/')
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: seo.label,
          item: canonicalUrl
        }
      ]
    })
  }
  document.documentElement.setAttribute('lang', seo.lang)
  document.documentElement.setAttribute('dir', seo.lang === 'ar' ? 'rtl' : 'ltr')
}
